import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { studioApi } from '../lib/api';
import { toast } from 'sonner';
import { SquarePen, Plus, Pencil, Trash2, ArrowRight, FileSignature, XCircle, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

// Solo 3 tipi di evento (semplice timeline)
const TIPI = ['Nuova SCIA', 'Voltura', 'Cessata Attività'];
const TIPO_META = {
  'Nuova SCIA': { icon: FileSignature, color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  'Voltura': { icon: ArrowRight, color: 'bg-amber-100 text-amber-800 border-amber-300' },
  'Cessata Attività': { icon: XCircle, color: 'bg-red-100 text-red-700 border-red-300' },
};

const empty = {
  cliente_id: '', cliente_precedente_id: '', tipo_pratica: 'Nuova SCIA',
  descrizione: '', indirizzo_installazione: '', comune: '',
  data_evento: new Date().toISOString().slice(0, 10), note: ''
};

const fmtDate = (s) => {
  if (!s) return '-';
  try { return new Date(s).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }); } catch { return s; }
};

export default function StudioInsegnePage() {
  const [items, setItems] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [filterTipo, setFilterTipo] = useState('__all__');
  const [search, setSearch] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const [i, c] = await Promise.all([studioApi.getInsegne(), studioApi.getClienti()]);
      setItems(i.data); setClienti(c.data);
    } catch { toast.error('Errore caricamento'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(empty); setEditingId(null); setOpen(true); };
  const openEdit = (i) => {
    setForm({ ...empty, ...i });
    setEditingId(i.id); setOpen(true);
  };
  const save = async () => {
    if (!form.cliente_id || !form.descrizione) { toast.error('Cliente attuale e descrizione obbligatori'); return; }
    if (form.tipo_pratica === 'Voltura' && !form.cliente_precedente_id) {
      toast.error('Per la Voltura specifica il cliente precedente');
      return;
    }
    const payload = { ...form };
    if (payload.tipo_pratica !== 'Voltura') payload.cliente_precedente_id = null;
    try {
      if (editingId) { await studioApi.updateInsegna(editingId, payload); toast.success('Aggiornata'); }
      else { await studioApi.createInsegna(payload); toast.success('Creata'); }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };
  const remove = async (i) => {
    if (!window.confirm(`Eliminare evento "${i.descrizione}"?`)) return;
    try { await studioApi.deleteInsegna(i.id); toast.success('Eliminata'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const filtered = useMemo(() => items.filter(i =>
    (filterTipo === '__all__' || i.tipo_pratica === filterTipo) &&
    (!search ||
      (i.descrizione || '').toLowerCase().includes(search.toLowerCase()) ||
      (i.cliente_nome || '').toLowerCase().includes(search.toLowerCase()) ||
      (i.cliente_precedente_nome || '').toLowerCase().includes(search.toLowerCase()) ||
      (i.indirizzo_installazione || '').toLowerCase().includes(search.toLowerCase()) ||
      (i.comune || '').toLowerCase().includes(search.toLowerCase())
    )
  ), [items, filterTipo, search]);

  return (
    <div className="space-y-4 p-4 lg:p-6" data-testid="studio-insegne-page">
      <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
        <div>
          <div className="flex items-center gap-2"><SquarePen className="w-6 h-6 text-indigo-600" /><h1 className="text-2xl font-bold">Insegne & Affissioni</h1><Badge>{items.length}</Badge></div>
          <p className="text-sm text-gray-600 mt-1">Storico Nuove SCIA, Volture e Cessazioni — sapere sempre chi è il titolare attuale dell'insegna</p>
        </div>
        <div className="flex gap-2 items-center">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca..." className="w-48" data-testid="search-insegne" />
          <Select value={filterTipo} onValueChange={setFilterTipo}>
            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tutti i tipi</SelectItem>
              {TIPI.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={openCreate} data-testid="new-insegna-btn"><Plus className="w-4 h-4 mr-1" /> Nuovo evento</Button>
        </div>
      </motion.div>

      {loading ? <p className="text-gray-500 italic">Caricamento...</p> : (
        <div className="space-y-2">
          {filtered.length === 0 && <p className="text-center text-gray-500 italic py-10">Nessun evento registrato.</p>}
          {filtered.map((i) => {
            const meta = TIPO_META[i.tipo_pratica] || TIPO_META['Nuova SCIA'];
            const Icon = meta.icon;
            return (
              <motion.div key={i.id} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="bg-white border rounded-lg p-3 shadow-sm flex items-center gap-3" data-testid={`insegna-card-${i.id}`}>
                <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center border ${meta.color}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={`text-xs ${meta.color}`}>{i.tipo_pratica}</Badge>
                    <span className="text-xs text-gray-500 font-mono">{i.codice}</span>
                    <span className="text-xs text-gray-500">· {fmtDate(i.data_evento)}</span>
                  </div>
                  <div className="font-semibold mt-0.5">{i.descrizione}</div>
                  <div className="text-sm text-gray-700 mt-0.5 flex items-center gap-1 flex-wrap">
                    {i.tipo_pratica === 'Voltura' && i.cliente_precedente_nome && (
                      <>
                        <span className="text-gray-500">Da:</span>
                        <span className="font-medium">{i.cliente_precedente_nome}</span>
                        <ArrowRight className="w-3.5 h-3.5 text-amber-600" />
                      </>
                    )}
                    <span className="text-gray-500">Titolare attuale:</span>
                    <span className="font-medium text-indigo-700">{i.cliente_nome || '-'}</span>
                  </div>
                  {(i.indirizzo_installazione || i.comune) && (
                    <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {[i.indirizzo_installazione, i.comune].filter(Boolean).join(', ')}
                    </div>
                  )}
                  {i.note && <div className="text-xs text-gray-500 italic mt-0.5">{i.note}</div>}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(i)} data-testid={`edit-insegna-${i.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(i)} data-testid={`delete-insegna-${i.id}`}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editingId ? 'Modifica evento' : 'Nuovo evento insegna'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label>Tipo evento *</Label>
              <Select value={form.tipo_pratica} onValueChange={(v) => setForm({ ...form, tipo_pratica: v })}>
                <SelectTrigger data-testid="insegna-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>{TIPI.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Data evento</Label>
              <Input type="date" value={form.data_evento || ''} onChange={(e) => setForm({ ...form, data_evento: e.target.value })} />
            </div>
            {form.tipo_pratica === 'Voltura' && (
              <div className="md:col-span-2">
                <Label>Cliente precedente (chi cedeva l'insegna) *</Label>
                <Select value={form.cliente_precedente_id || '__none__'} onValueChange={(v) => setForm({ ...form, cliente_precedente_id: v === '__none__' ? '' : v })}>
                  <SelectTrigger data-testid="insegna-cliente-precedente"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">-- nessuno --</SelectItem>
                    {clienti.map(c => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="md:col-span-2">
              <Label>{form.tipo_pratica === 'Voltura' ? 'Cliente attuale / nuovo titolare *' : 'Cliente / Titolare *'}</Label>
              <Select value={form.cliente_id || '__none__'} onValueChange={(v) => setForm({ ...form, cliente_id: v === '__none__' ? '' : v })}>
                <SelectTrigger data-testid="insegna-cliente"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">-- nessuno --</SelectItem>
                  {clienti.map(c => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2"><Label>Descrizione insegna *</Label>
              <Input placeholder="Es. Insegna luminosa via Roma 12 — bar 'Centrale'" value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} data-testid="insegna-descrizione" />
            </div>
            <div><Label>Indirizzo installazione</Label><Input value={form.indirizzo_installazione} onChange={(e) => setForm({ ...form, indirizzo_installazione: e.target.value })} /></div>
            <div><Label>Comune</Label><Input value={form.comune} onChange={(e) => setForm({ ...form, comune: e.target.value })} /></div>
            <div className="md:col-span-2"><Label>Note</Label><Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="save-insegna-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
